import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export interface SummaryItem {
  label: string;
  value: string | number | React.ReactNode;
  highlight?: boolean;
}

export interface SummarySection {
  title: string;
  items: SummaryItem[];
}

export interface ProgressSummaryPanelProps {
  title?: string;
  sections: SummarySection[];
  totalItems?: number;
  completedItems?: number;
  showProgress?: boolean;
  inProgress?: boolean;
}

export const ProgressSummaryPanel: React.FC<ProgressSummaryPanelProps> = ({
  title = 'Summary',
  sections,
  totalItems,
  completedItems,
  showProgress = false,
  inProgress = false
}) => {
  const progressPercent = totalItems && completedItems !== undefined
    ? Math.round((completedItems / totalItems) * 100)
    : 0;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {showProgress && totalItems !== undefined && completedItems !== undefined && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {completedItems} / {totalItems} items
            </Typography>
          </Box>
          <LinearProgress
            variant={inProgress ? 'indeterminate' : 'determinate'}
            value={progressPercent}
            sx={{ height: 8, borderRadius: 1 }}
          />
          {!inProgress && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {progressPercent}% complete
            </Typography>
          )}
        </Box>
      )}

      {sections.map((section, sectionIndex) => (
        <Box key={sectionIndex} sx={{ mb: sectionIndex < sections.length - 1 ? 3 : 0 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary">
            {section.title}
          </Typography>
          <List dense disablePadding>
            {section.items.map((item, itemIndex) => (
              <ListItem
                key={itemIndex}
                disableGutters
                sx={{
                  py: 0.5,
                  borderBottom: itemIndex < section.items.length - 1 ? 1 : 0,
                  borderColor: 'divider'
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {item.label}
                      </Typography>
                      {item.highlight ? (
                        <Chip
                          label={item.value}
                          size="small"
                          color="primary"
                          sx={{ fontWeight: 'bold' }}
                        />
                      ) : typeof item.value === 'string' || typeof item.value === 'number' ? (
                        <Typography variant="body2" fontWeight="bold">
                          {item.value}
                        </Typography>
                      ) : (
                        item.value
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}

      {!inProgress && completedItems !== undefined && totalItems !== undefined && completedItems === totalItems && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: 'success.light',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <CheckCircleIcon color="success" />
          <Typography variant="body2" fontWeight="bold">
            All items processed successfully!
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
